import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MessageCircle, Send, X } from 'lucide-react';
import MoonLogo from './MoonLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// ?start=support faz o Telegram abrir o bot já no fluxo de suporte (ver
// backend/api/support.py) em vez da tela genérica.
const TELEGRAM_FALLBACK = 'https://t.me/ArbXSignalsBot?start=support';

const GREETING = {
  role: 'assistant',
  content: "Hi! I'm the moon.land assistant. Ask me anything about plans, signals, the Telegram bot or arbitrage basics.",
};

// Bolha de suporte AI-first: o Claude responde no site; humano é último caso,
// via /support no bot de Telegram. Se a IA estiver desligada no backend, a
// bolha vira um atalho direto para o Telegram — o suporte nunca fica mudo.
const SupportWidget = () => {
  const [open, setOpen] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [telegram, setTelegram] = useState(TELEGRAM_FALLBACK);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/support/config`)
      .then(({ data }) => {
        if (typeof data?.ai_enabled === 'boolean') setAiEnabled(data.ai_enabled);
        if (data?.telegram) setTelegram(data.telegram);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    // o histórico enviado exclui a saudação local e fica nos últimos 11
    // turnos — o backend rejeita mais de 12 mensagens
    const history = [...messages.filter(m => m !== GREETING), { role: 'user', content: text.slice(0, 1500) }];
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setBusy(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/support/chat`, {
        messages: history.slice(-11),
      });
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: typeof detail === 'string' ? detail : `Something went wrong. Reach us on Telegram: ${telegram}`,
      }]);
    } finally {
      setBusy(false);
    }
  };

  // IA desligada: a bolha abre o Telegram directamente.
  if (!aiEnabled) {
    return (
      <a
        href={telegram}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-[#f2e2a6] flex items-center justify-center shadow-lg shadow-black/40 hover:scale-105 transition-transform"
        aria-label="Support on Telegram"
      >
        <MessageCircle className="w-6 h-6 text-black" />
      </a>
    );
  }

  return (
    <>
      {open && (
        <div className="fixed bottom-24 right-5 z-50 w-[min(22rem,calc(100vw-2.5rem))] h-[28rem] flex flex-col bg-[#0b0e1a] border border-white/10 rounded-2xl shadow-2xl shadow-black/60 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-black/40 border-b border-white/10">
            <div className="flex items-center space-x-2">
              <MoonLogo size={22} />
              <span className="text-white text-sm font-semibold">moon.land support</span>
            </div>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm whitespace-pre-wrap ${
                  m.role === 'user'
                    ? 'bg-[#f2e2a6] text-black'
                    : 'bg-white/10 text-white'
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && <div className="text-gray-500 text-xs">thinking…</div>}
          </div>

          <div className="p-3 border-t border-white/10">
            <div className="flex items-center space-x-2">
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && send()}
                placeholder="Ask about plans, signals, the bot…"
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#f2e2a6]/50"
                maxLength={1500}
              />
              <button
                onClick={send}
                disabled={busy || !input.trim()}
                className="w-9 h-9 rounded-lg bg-[#f2e2a6] flex items-center justify-center disabled:opacity-40"
                aria-label="Send"
              >
                <Send className="w-4 h-4 text-black" />
              </button>
            </div>
            <a
              href={telegram}
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-2 text-center text-xs text-gray-500 hover:text-[#f2e2a6]"
            >
              Need a human? /support on Telegram →
            </a>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-[#f2e2a6] flex items-center justify-center shadow-lg shadow-black/40 hover:scale-105 transition-transform"
        aria-label="Support chat"
        data-testid="support-widget-button"
      >
        {open ? <X className="w-6 h-6 text-black" /> : <MessageCircle className="w-6 h-6 text-black" />}
      </button>
    </>
  );
};

export default SupportWidget;
